import React, { useState, useEffect, useRef } from "react";
import apiService from "../../services/api/api.service";
import { toast } from "react-hot-toast";
import { Loader, CheckCircle, X } from "lucide-react";

const CompanyOverview = ({ uniqueTitle }) => {
  const [basicData, setBasicData] = useState({});
  const [editValue, setEditValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState(null);

  const modalRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const fetchCompanyData = async () => {
      setLoading(true);
      try {
        const result = await apiService.getProductByTitle(uniqueTitle);
        if (result?.success && result?.data?.basicData) {
          setBasicData(result.data.basicData);
          setEditValue(result.data.basicData.aboutCompany || "");
        } else {
          throw new Error("Company data not found in API response");
        }
      } catch (err) {
        setError(err.message);
        showToast(err.message, "error");
      } finally {
        setLoading(false);
      }
    };

    if (uniqueTitle) fetchCompanyData();
  }, [uniqueTitle]);

  useEffect(() => {
    // Focus the textarea when modal opens
    if (modalOpen && textareaRef.current) {
      textareaRef.current.focus();
    }

    // Handle escape key press to close modal
    const handleEscKey = (e) => {
      if (e.key === "Escape" && modalOpen) {
        setModalOpen(false);
      }
    };

    // Handle click outside modal to close it
    const handleOutsideClick = (e) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target) &&
        modalOpen
      ) {
        setModalOpen(false);
      }
    };

    if (modalOpen) {
      document.addEventListener("keydown", handleEscKey);
      document.addEventListener("mousedown", handleOutsideClick);
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.removeEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "auto";
    };
  }, [modalOpen]);

  // Custom toast notification function
  const showToast = (message, type = "success") => {
    // Create a custom toast with styling
    toast.custom((t) => (
      <div
        className={`${
          t.visible ? 'animate-enter' : 'animate-leave'
        } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
      >
        <div className={`flex-1 w-0 p-4 ${type === "success" ? "bg-green-50" : "bg-red-50"}`}>
          <div className="flex items-start">
            <div className={`flex-shrink-0 pt-0.5 ${type === "success" ? "text-green-400" : "text-red-400"}`}>
              {type === "success" ? <CheckCircle size={20} /> : <X size={20} />}
            </div>
            <div className="ml-3 flex-1">
              <p className={`text-sm font-medium ${type === "success" ? "text-green-800" : "text-red-800"}`}>
                {type === "success" ? "Success" : "Error"}
              </p>
              <p className={`mt-1 text-sm ${type === "success" ? "text-green-600" : "text-red-600"}`}>
                {message}
              </p>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="ml-4 flex-shrink-0 bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <span className="sr-only">Close</span>
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    ), {
      duration: 4000,
      position: 'bottom-right',
    });
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const updatedData = {
        basicData: {
          ...basicData,
          aboutCompany: editValue,
        },
      };

      await apiService.updateProduct(uniqueTitle, updatedData);
      setBasicData(updatedData.basicData);
      setModalOpen(false);
      showToast("Company overview updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to update company overview", "error");
    } finally {
      setUpdating(false);
    }
  };

  const openModal = () => {
    setEditValue(basicData.aboutCompany || "");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  if (loading)
    return <Loader className="animate-spin text-indigo-500 mb-4" size={36} />;
  if (error)
    return <div className="p-4 text-red-500 text-center">Error: {error}</div>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <h2 className="text-lg sm:text-xl font-bold mb-4 text-gray-800">
        Company Overview
      </h2>

      <div className="text-gray-700 leading-relaxed text-sm sm:text-base mb-4">
        <p>{basicData.aboutCompany || "No overview available"}</p>
      </div>

      <button
        onClick={openModal}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-label="Edit company overview"
      >
        Edit Overview
      </button>

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-overview-title"
        >
          <div
            ref={modalRef}
            className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 sm:mx-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 id="edit-overview-title" className="text-lg font-semibold">
                Edit Company Overview
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label="Close modal"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>

            <textarea
              ref={textareaRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              rows={6}
              className="w-full border border-gray-300 rounded-md p-3 mb-4 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter company overview..."
              aria-label="Company overview text"
            />

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                disabled={updating}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyOverview;